import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import AlertBanner from '@/components/common/AlertBanner/alert-banner.vue'
import {
    initialManualShipmentModalState,
    ManualShipmentModalPortion,
    ManualShipmentModalSample,
    ManualShipmentModalState,
    ManualShipmentModalSubportion
} from '@/components/manual-shipment/manual-shipment.model'
import { compatibleShipmentRuleFinderService } from '@/api/resources/shipments/compatibleShipmentRuleFinder/compatible-shipment-rule-finder.service'
import { CommonSampleFamilyTreeRequest } from '@/api/resources/shipments/compatibleShipmentRuleFinder/compatible-shipment-rule-finder.dto'
import { ManualShipmentRuleResponse } from '@/api/services/common.dto'
import { cloneDeep } from 'lodash'
import { sampleState } from '@/api/resources/entityState/sample-state'
import TranslatedEntityBadge from '@/components/common/TranslatedEntityBadge.vue'
import { portionState } from '@/api/resources/entityState/portion-state'
import { subportionState } from '@/api/resources/entityState/subportion-state'
import { manualShipmentRuleApplierService } from '@/components/manual-shipment/manual-shipment.service'
import { ManualShipmentRuleApplyRequest } from '@/components/manual-shipment/manual-shipment.dto'
import {
    CommonPortionFamilyTreeRequest,
    CommonSubportionFamilyTreeRequest
} from '@/api/resources/common/shared/models/common.models'
import { EntityEnum } from '@/api/resources/entity/entity'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: {
        AlertBanner,
        TranslatedEntityBadge
    }
})
export default class ManualShipment extends Vue {
    @Ref() readonly manualShipment!: any

    @Prop({ default: () => [] }) readonly samples!: ManualShipmentModalSample[]
    @Prop({ default: () => [] }) readonly portions!: ManualShipmentModalPortion[]
    @Prop({ default: () => [] }) readonly subportions!: ManualShipmentModalSubportion[]
    @Prop({ default: false }) readonly isLaboratoryDesktopPendingCut!: boolean

    private state !: ManualShipmentModalState

    constructor() {
        super()
        this.state = initialManualShipmentModalState()
    }

    get visibleLaboratoryCutWarning(): boolean {
        return this.isLaboratoryDesktopPendingCut
    }

    get laboratoryId(): number {
        return this.$store.getters.labId
    }

    get entityEnum() {
        return EntityEnum
    }

    get entityType(): StrictNullable<EntityEnum> {
        if (this.samples.length) {
            return EntityEnum.SAMPLE
        } else if (this.portions.length) {
            return EntityEnum.PORTION
        } else if (this.subportions.length) {
            return EntityEnum.SUBPORTION
        } else {
            return null
        }
    }

    async findSamplesCompatibleManualShipmentRules(samples: ManualShipmentModalSample[], laboratoryId: number): Promise<ManualShipmentRuleResponse[]> {
        const request:CommonSampleFamilyTreeRequest[] = samples.map(sample => ({
            caseId: sample.caseId,
            sampleId: sample.sampleId
        }))
        const response = await this.$_toCallUnique(compatibleShipmentRuleFinderService.findSamplesCompatibleManualShipmentRules(request, laboratoryId))
        return response ?? []
    }
    async findPortionsCompatibleManualShipmentRules(portions: ManualShipmentModalPortion[], laboratoryId: number): Promise<ManualShipmentRuleResponse[]> {
        const request:CommonSampleFamilyTreeRequest[] = portions.map(portion => ({
            caseId: portion.caseId,
            sampleId: portion.sampleId,
            portionId: portion.portionId
        }))
        const response = await this.$_toCallUnique(compatibleShipmentRuleFinderService.findPortionsCompatibleManualShipmentRules(request, laboratoryId))
        return response ?? []
    }
    async findSubportionsCompatibleManualShipmentRules(subportions: ManualShipmentModalSubportion[], laboratoryId: number): Promise<ManualShipmentRuleResponse[]> {
        const request:CommonSampleFamilyTreeRequest[] = subportions.map(subportion => ({
            caseId: subportion.caseId,
            sampleId: subportion.sampleId,
            portionId: subportion.portionId,
            subportionId: subportion.subportionId
        }))
        const response = await this.$_toCallUnique(compatibleShipmentRuleFinderService.findSubportionsCompatibleManualShipmentRules(request, laboratoryId))
        return response ?? []
    }

    async removeElement(index: number): Promise<void> {
        this.state.selectedElements.splice(index, 1)
        if (!this.state.selectedElements.length) {
            this.hide()
        } else {
            await this.findCompatibleManualShipmentRules()
        }
    }

    async applyManualRuleOverSamples(manualShipmentRuleId: number): Promise<void> {
        const request: ManualShipmentRuleApplyRequest<CommonSampleFamilyTreeRequest> = {
            entityIdentifiers: this.state.selectedElements.map(element => ({
                caseId: element.caseId,
                sampleId: element.sampleId
            })),
            manualShipmentRuleId: manualShipmentRuleId
        }
        const response = await this.$_toCallUnique(manualShipmentRuleApplierService.applyOverSamples(request, this.laboratoryId))
        if (response) {
            this.$_showToastSuccess(this.$tc('component.manualShipment.toastrSuccess', 1))
        }
    }

    async applyManualRuleOverPortions(manualShipmentRuleId: number): Promise<void> {
        const request: ManualShipmentRuleApplyRequest<CommonPortionFamilyTreeRequest> = {
            entityIdentifiers: this.state.selectedElements.map(element => ({
                caseId: element.caseId,
                portionId: element.portionId,
                sampleId: element.sampleId
            })),
            manualShipmentRuleId: manualShipmentRuleId
        }
        const response = await this.$_toCallUnique(manualShipmentRuleApplierService.applyOverPortions(request, this.laboratoryId))
        if (response) {
            this.$_showToastSuccess(this.$tc('component.manualShipment.toastrSuccess', 1))
        }
    }

    async applyManualRuleOverSubportions(manualShipmentRuleId: number): Promise<void> {
        const request: ManualShipmentRuleApplyRequest<CommonSubportionFamilyTreeRequest> = {
            entityIdentifiers: this.state.selectedElements.map(element => ({
                caseId: element.caseId,
                portionId: element.portionId,
                sampleId: element.sampleId,
                subportionId: element.subportionId
            })),
            manualShipmentRuleId: manualShipmentRuleId
        }
        const response = await this.$_toCallUnique(manualShipmentRuleApplierService.applyOverSubportions(request, this.laboratoryId))
        if (response) {
            this.$_showToastSuccess(this.$tc('component.manualShipment.toastrSuccess', 1))
        }
    }

    async applyManualShipment(manualShipmentRuleId: number): Promise<void> {
        if (this.samples.length) {
            await this.applyManualRuleOverSamples(manualShipmentRuleId)
        } else if (this.portions.length) {
            await this.applyManualRuleOverPortions(manualShipmentRuleId)
        } else if (this.subportions.length) {
            await this.applyManualRuleOverSubportions(manualShipmentRuleId)
        }
        this.$emit('manualShipmentApplied')
        this.hide()
    }

    getSelectedElements(): void {
        if (this.samples.length) {
            this.state.selectedElements = cloneDeep(this.samples)
            this.state.entityStates = sampleState.states
        } else if (this.portions.length) {
            this.state.selectedElements = cloneDeep(this.portions)
            this.state.entityStates = portionState.states
        } else if (this.subportions.length) {
            this.state.selectedElements = cloneDeep(this.subportions)
        }
    }

    async findCompatibleManualShipmentRules(): Promise<void> {
        if (this.samples.length) {
            await sampleState.loadStates()
            this.state.compatibleManualShipmentRules = await this.findSamplesCompatibleManualShipmentRules(this.state.selectedElements, this.laboratoryId)
            this.state.entityStates = sampleState.states
        } else if (this.portions.length) {
            await portionState.loadStates()
            this.state.compatibleManualShipmentRules = await this.findPortionsCompatibleManualShipmentRules(this.state.selectedElements, this.laboratoryId)
            this.state.entityStates = portionState.states
        } else if (this.subportions.length) {
            await subportionState.loadStates()
            this.state.compatibleManualShipmentRules = await this.findSubportionsCompatibleManualShipmentRules(this.state.selectedElements, this.laboratoryId)
            this.state.entityStates = subportionState.states
        }
    }

    async show(): Promise<void> {
        this.getSelectedElements()
        await this.findCompatibleManualShipmentRules()
        this.manualShipment.show()
    }

    hide(): void {
        this.manualShipment.hide()
    }
}
